// /app/frontend/src/pages/seo/HowToFindSisterWifePage.jsx
// Cluster cornerstone: how-to / educational long-form page with FAQ schema.
import React from 'react';
import PublicSEOHeader from '../../components/PublicSEOHeader';
import {
  SEOHead,
  organizationSchema,
  buildFaqSchema,
  buildBreadcrumbSchema,
  GradientHero,
  StepCard,
  FaqList,
  RelatedGuides,
  BottomCTA,
} from './SEOUtils';

const FAQS = [
  {
    q: 'What is a sister wife?',
    a: 'A sister wife is a woman who is part of a plural family — typically a relationship structure where one husband shares emotional, romantic, and household life with multiple committed wives, all of whom know about and consent to the arrangement. Sister wives often form deep bonds with one another, and the structure works best when communication, trust, and shared values come first.',
  },
  {
    q: 'How do couples find a sister wife?',
    a: 'Most couples start by getting clear on their own relationship — what they want, what their boundaries are, and what kind of person they\'re inviting in. From there, the path usually involves community spaces designed for intentional poly and plural family-building, like UnityLove, where couples and singles can connect honestly about long-term intentions instead of casual dating.',
  },
  {
    q: 'How long does the process take?',
    a: 'There is no single timeline. Some families connect with a sister wife within months; for others, it takes years of conversation, trust-building, and intentional dating. Healthy sister-wife relationships are built on emotional readiness, not deadlines. The slower path is almost always the stronger one.',
  },
  {
    q: 'What makes a healthy sister-wife relationship?',
    a: 'Clear communication, full consent from everyone involved, defined boundaries, shared long-term goals, financial transparency, and a foundation of friendship — not just romance. The strongest plural families also build in regular check-ins, individual time, and a willingness to renegotiate as the relationship grows.',
  },
];

export default function HowToFindSisterWifePage() {
  const path = '/how-to-find-a-sister-wife';
  const schemas = [
    organizationSchema,
    buildFaqSchema(FAQS),
    buildBreadcrumbSchema([
      { name: 'Home', url: '/' },
      { name: 'Guides', url: '/guides' },
      { name: 'How To Find A Sister Wife', url: path },
    ]),
  ];

  return (
    <div className="min-h-screen bg-white">
      <PublicSEOHeader />
      <SEOHead
        title="How To Find A Sister Wife"
        description="Learn practical and respectful approaches to finding a compatible sister wife, building trust, and creating healthy family dynamics."
        canonical={path}
        schemas={schemas}
      />

      <GradientHero
        eyebrow="A UnityLove Guide"
        headline="How To Find A Sister Wife"
        sub="Building a plural family is a slow, honest, deeply intentional process. Here's a respectful guide to where to start."
        body="This guide is for couples and singles who are exploring sister-wife family structures and want a healthy, communication-first foundation."
        primaryCta={{ label: 'Create Your UnityLove Profile', to: '/auth?mode=signup' }}
        secondaryCta={{ label: 'Browse Related Guides', to: '/guides' }}
      />

      <main className="max-w-3xl mx-auto px-4 py-14">
        {/* Intro */}
        <section className="mb-12">
          <p className="text-lg text-slate-700 leading-relaxed">
            Finding a sister wife is not the same as casual dating. It’s the beginning of building a family — and like any family worth having, it deserves time, intentionality, and honest conversation from day one. The pages below outline the six biggest steps most healthy plural families share, plus the common mistakes that derail the early stages.
          </p>
        </section>

        {/* Step 1 — Relationship readiness */}
        <section className="mb-10">
          <h2 className="font-playfair text-3xl font-bold text-slate-900 mb-3">
            1. Start With Relationship Readiness
          </h2>
          <p className="text-slate-700 leading-relaxed mb-3">
            Before looking outward, look inward. The strongest plural families begin with an existing couple (or single) whose own foundation is healthy — not one that’s hoping a new partner will fix unresolved tension. Ask honestly:
          </p>
          <ul className="list-disc list-inside text-slate-700 space-y-1 mb-3">
            <li>Do we communicate openly when something is uncomfortable?</li>
            <li>Are we both genuinely wanting this — or is one partner agreeing to keep the peace?</li>
            <li>Have we worked through past resentments, jealousy, or trust wounds?</li>
            <li>Do we have the emotional bandwidth for a new person right now?</li>
          </ul>
          <p className="text-slate-700 leading-relaxed">
            If any answer is shaky, that’s the work to do first. A new partner cannot heal an existing relationship; she can only enter it.
          </p>
        </section>

        {/* Step 2 — Define family goals */}
        <section className="mb-10">
          <h2 className="font-playfair text-3xl font-bold text-slate-900 mb-3">
            2. Define Your Family Goals
          </h2>
          <p className="text-slate-700 leading-relaxed mb-3">
            Plural families come in many shapes. Some live under one roof. Some maintain separate households. Some plan children together; others don’t. Some practice spiritually grounded polygamy; others come from a secular ENM tradition. Before you start looking, get clear on what you want and what you don’t.
          </p>
          <p className="text-slate-700 leading-relaxed">
            Write it down together. Long-term goals, financial vision, faith framework (if any), parenting plans, sleeping arrangements, time-sharing rhythms. The clearer your blueprint, the easier it becomes to recognize when someone is — or isn’t — a fit.
          </p>
        </section>

        {/* Step 3 — Communication first */}
        <section className="mb-10">
          <h2 className="font-playfair text-3xl font-bold text-slate-900 mb-3">
            3. Communication First, Always
          </h2>
          <p className="text-slate-700 leading-relaxed">
            In monogamous dating, chemistry can precede conversation. In sister-wife family-building, that order has to flip. Long talks come first — about expectations, fears, past relationships, and visions for the future — and physical or romantic intimacy comes later. Every healthy plural family we’ve spoken to says the same thing: <strong>over-communicate, especially in the first year.</strong>
          </p>
        </section>

        {/* Step 4 — Trust slowly */}
        <section className="mb-10">
          <h2 className="font-playfair text-3xl font-bold text-slate-900 mb-3">
            4. Build Trust Slowly
          </h2>
          <p className="text-slate-700 leading-relaxed">
            Trust in a plural family isn’t a single moment — it’s a hundred small ones. Showing up when you said you would. Following through on agreements about time. Telling the truth even when it’s awkward. The temptation early on is to fast-track the relationship because of how good the chemistry feels. Resist that. The families that thrive are the ones that gave themselves the grace of time.
          </p>
        </section>

        {/* Step 5 — Mistakes */}
        <section className="mb-10">
          <h2 className="font-playfair text-3xl font-bold text-slate-900 mb-3">
            5. Common Mistakes To Avoid
          </h2>
          <div className="grid gap-3">
            {[
              ['Hiding the search', 'A sister wife should never be a surprise to one partner. If one person doesn\'t know what\'s being explored, that\'s an affair — not poly.'],
              ['Skipping the work', 'Searching for a third before doing the personal and couple work usually ends badly. Heal first, build second.'],
              ['One person leading', 'When one partner is significantly more enthusiastic than the other, slow down. Real consent is not enthusiasm to make someone else happy.'],
              ['Rushing physical intimacy', 'Plural family-building moves slower than dating. If she\'s spending the night before you\'ve discussed boundaries, the order is off.'],
              ['Ignoring the existing partner', 'The new connection often feels electric. Continue investing in the existing relationship at the same time — not less.'],
            ].map(([title, body]) => (
              <div key={title} className="rounded-xl border border-amber-200 bg-amber-50 p-4">
                <p className="font-semibold text-amber-900">{title}</p>
                <p className="text-amber-900/80 mt-1 text-sm">{body}</p>
              </div>
            ))}
          </div>
        </section>

        {/* Step 6 — Healthy foundation */}
        <section className="mb-12">
          <h2 className="font-playfair text-3xl font-bold text-slate-900 mb-3">
            6. Creating A Healthy Foundation
          </h2>
          <p className="text-slate-700 leading-relaxed">
            A strong sister-wife relationship rests on the same pillars as any committed family: shared values, mutual respect, financial transparency, defined time, room for individual growth, and a willingness to renegotiate as life changes. None of this is unique to plural families — they just require all of it more deliberately, because there are more people whose needs matter.
          </p>
        </section>

        {/* Process timeline */}
        <section className="mb-14">
          <h2 className="font-playfair text-3xl font-bold text-slate-900 mb-6">
            The Honest Timeline
          </h2>
          <div className="grid gap-3">
            <StepCard n={1} title="Months 1–3 — Conversation only" body="Get to know each other through long, honest conversations. No pressure for intimacy or commitment." />
            <StepCard n={2} title="Months 3–6 — Family meetings" body="Spend time with the existing couple as a group. Notice how she fits into the dynamic — and how the dynamic shifts." />
            <StepCard n={3} title="Months 6–12 — Trial commitments" body="Start practicing the actual life — shared time, shared decisions, shared boundaries. Adjust as needed." />
            <StepCard n={4} title="Year 1+ — Full integration" body="Move toward whatever form of family you've all defined together. Continue regular check-ins; relationships are never finished." />
          </div>
        </section>

        {/* FAQ */}
        <section className="mb-4">
          <h2 className="font-playfair text-3xl font-bold text-slate-900 mb-2">
            Frequently Asked Questions
          </h2>
          <p className="text-slate-600 mb-6">
            Quick answers to the questions we hear most often.
          </p>
          <FaqList items={FAQS} />
        </section>
      </main>

      <RelatedGuides currentPath={path} />
      <BottomCTA />
    </div>
  );
}
